import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  FiX, FiChevronLeft, FiChevronRight, FiDownload, FiZoomIn, FiZoomOut,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { InspectedArea } from "../../../logic/api";
import { theme } from "../../../theme";
import splat from "../../../assets/splat.jpg";

const ACCENT = theme.palette.brand.main;
const ZONE_COUNT = 12;
const zoneLabel = (t: (key: string) => string, i: number) => t(`complaints.gridReference.${i}`);

// Hotspot rects in splat.jpg coordinate space (~202 x 255).
const ZONE_RECTS = [
  { x: 0, y: 0, w: 66, h: 80 }, { x: 66, y: 0, w: 66, h: 80 }, { x: 136, y: 0, w: 66, h: 80 },
  { x: 0, y: 80, w: 66, h: 50 }, { x: 66, y: 80, w: 66, h: 50 }, { x: 136, y: 80, w: 66, h: 50 },
  { x: 0, y: 130, w: 66, h: 50 }, { x: 66, y: 130, w: 66, h: 50 }, { x: 136, y: 130, w: 66, h: 50 },
  { x: 0, y: 175, w: 66, h: 80 }, { x: 66, y: 175, w: 66, h: 80 }, { x: 136, y: 175, w: 66, h: 80 },
];
const DIAGRAM_W = 202, DIAGRAM_H = 255;

const hhmm = (iso: string) => {
  const t = iso.split("T")[1] ?? "";
  return t.slice(0, 8) || iso;
};

export const zoneIndexOf = (area: InspectedArea) => {
  const zoneIndex = Number(area.gridReference);
  return Number.isInteger(zoneIndex) && zoneIndex >= 0 && zoneIndex < ZONE_COUNT
    ? zoneIndex
    : null;
};

export const groupAreasByZone = (areas: InspectedArea[]) => {
  const byZone: InspectedArea[][] = Array.from({ length: ZONE_COUNT }, () => []);
  areas.forEach(a => {
    const zoneIndex = zoneIndexOf(a);
    if (zoneIndex != null) byZone[zoneIndex].push(a);
  });
  const counts = byZone.map(z => z.length);
  return { byZone, counts, maxPerZone: counts.reduce((m, c) => Math.max(m, c), 0) };
};

// photo count picks the layout: >2 -> new viewer (flat grid), >=4 -> car-diagram switcher
export const NEW_VIEWER_MIN_PER_ZONE = 2;
export const DIAGRAM_MIN_PER_ZONE = 4;

const FrameThumb: React.FC<{ url: string; label: string; index: number; onOpen: () => void }> = ({ url, label, index, onOpen }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [url]);

  return (
    <Box
      role="button" tabIndex={loaded ? 0 : -1} aria-disabled={!loaded} aria-label={`Open frame ${index + 1} (${label})`}
      onClick={loaded ? onOpen : undefined}
      onKeyDown={(e) => { if (loaded && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } }}
      sx={{
        "@keyframes zvFadeUp": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "none" } },
        "@keyframes zvFade": { from: { opacity: 0 }, to: { opacity: 1 } },
        position: "relative", cursor: loaded ? "pointer" : "default", aspectRatio: "4 / 3",
        borderRadius: "8px", overflow: "hidden", bgcolor: "#1b1b1f",
        animation: loaded ? "zvFadeUp .28s cubic-bezier(.2,.7,.3,1) both" : "none",
        animationDelay: `${Math.min(index * 18, 320)}ms`,
        transition: "transform .14s cubic-bezier(.2,.7,.3,1), outline-color .14s",
        outline: "2px solid transparent", outlineOffset: 2,
        "@media (hover:hover) and (pointer:fine)": {
          "&:hover": loaded ? { transform: "scale(1.02)", outlineColor: ACCENT } : {},
        },
        "&:active": loaded ? { transform: "scale(.97)" } : {},
        "&:focus-visible": loaded ? { outlineColor: ACCENT } : {},
        "@media (prefers-reduced-motion: reduce)": {
          animation: loaded ? "zvFade .2s ease both" : "none",
          transition: "outline-color .14s",
          "&:hover, &:active": { transform: "none" },
        },
      }}
    >
      {url && <img src={url} loading="lazy"
        alt={`Inspection frame ${index + 1}: ${label}`}
        onLoad={() => setLoaded(true)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity: loaded ? 1 : 0,
          transition: "opacity .12s ease-out",
        }} />}
    </Box>
  );
};

const CarDiagram: React.FC<{
  counts: number[]; selected: number; onSelect: (z: number) => void; width: number;
}> = ({ counts, selected, onSelect, width }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const h = (width / DIAGRAM_W) * DIAGRAM_H;
  return (
    <Box sx={{ position: "relative", width, height: h, mx: "auto" }}>
      <img src={splat} alt="" aria-hidden="true" style={{ width: "100%", height: "100%", display: "block" }} />
      {ZONE_RECTS.map((r, i) => {
        const count = counts[i] ?? 0;
        const empty = count === 0;
        const isSel = selected === i;
        return (
          <Box
            component="button"
            type="button"
            disabled={empty}
            key={i}
            onClick={() => !empty && onSelect(i)}
            aria-label={`${zoneLabel(t, i)}: ${count} ${count === 1 ? "photo" : "photos"}`}
            title={`${zoneLabel(t, i)} · ${count}`}
            sx={{
              position: "absolute",
              left: `${(r.x / DIAGRAM_W) * 100}%`, top: `${(r.y / DIAGRAM_H) * 100}%`,
              width: `${(r.w / DIAGRAM_W) * 100}%`, height: `${(r.h / DIAGRAM_H) * 100}%`,
              display: "flex", alignItems: "center", justifyContent: "center",
              p: 0, border: 0, bgcolor: "transparent",
              cursor: empty ? "default" : "pointer",
              outline: "2px solid transparent", outlineOffset: 2,
              transition: "transform .1s cubic-bezier(.23,1,.32,1)",
              "&:active": empty ? {} : { transform: "scale(.96)" },
              "&:focus-visible": empty ? {} : { outlineColor: ACCENT },
            }}
          >
            {!empty && (
              <Box sx={{
                minWidth: 26, height: 23, px: 0.75, borderRadius: "12px", lineHeight: 1,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: isSel ? 800 : 700,
                bgcolor: isSel ? ACCENT : "#fff",
                color: isSel ? theme.palette.brand.contrastText : theme.palette.text.primary,
                border: `1px solid ${isSel ? ACCENT : theme.palette.divider}`,
                transform: isSel ? "scale(1.12)" : "none",
                transition: "background-color .16s cubic-bezier(.23,1,.32,1), transform .12s cubic-bezier(.23,1,.32,1), border-color .16s",
                "@media (hover:hover) and (pointer:fine)": {
                  "&:hover": isSel ? {} : { borderColor: ACCENT, transform: "scale(1.06)" },
                },
              }}>{count}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// Memoized so the lightbox's pan/zoom re-renders don't re-render the strip.
const Filmstrip = React.memo<{
  frames: InspectedArea[]; index: number; getUrl: (f: string) => string; onSelect: (i: number) => void;
}>(({ frames, index, getUrl, onSelect }) => (
  <Box sx={{ display: "flex", gap: 1, p: 1, overflowX: "auto", bgcolor: "rgba(0,0,0,.5)" }}>
    {frames.map((f, i) => (
      <Box key={f.id} component="button" type="button" onClick={() => onSelect(i)}
        aria-label={`Show frame ${i + 1}`}
        sx={{
          flex: "0 0 auto", width: 72, height: 54, borderRadius: 1, overflow: "hidden", cursor: "pointer",
          p: 0, border: 0, bgcolor: "transparent",
          outline: i === index ? `2px solid ${ACCENT}` : "none", opacity: i === index ? 1 : 0.55,
          transition: "opacity .15s ease-out",
          "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 }
        }}>
        <img src={getUrl(f.photoFileName)} loading="lazy" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </Box>
    ))}
  </Box>
));
Filmstrip.displayName = "Filmstrip";

const Lightbox: React.FC<{
  frames: InspectedArea[]; getUrl: (f: string) => string;
  index: number; onIndex: (i: number) => void; onClose: () => void;
}> = ({ frames, getUrl, index, onIndex, onClose }) => {
  const { t } = useTranslation();
  const MAX_ZOOM = 6;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const stageRef = useRef<HTMLDivElement>(null);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const panStart = useRef<{ x: number; y: number } | null>(null);
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null);
  const TAP_SLOP = 6;
  const downPoint = useRef<{ x: number; y: number } | null>(null);
  const moved = useRef(false);
  const pinched = useRef(false);
  const frame = frames[index];
  const url = getUrl(frame.photoFileName);

  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const go = (d: number) => { reset(); onIndex((index + d + frames.length) % frames.length); };
  const selectFrame = useCallback((i: number) => { setZoom(1); setPan({ x: 0, y: 0 }); onIndex(i); }, [onIndex]);

  const clampPan = (p: { x: number; y: number }, z: number) => {
    const el = stageRef.current;
    if (!el || z <= 1) return { x: 0, y: 0 };
    const maxX = (el.clientWidth * (z - 1)) / 2;
    const maxY = (el.clientHeight * (z - 1)) / 2;
    return { x: Math.max(-maxX, Math.min(maxX, p.x)), y: Math.max(-maxY, Math.min(maxY, p.y)) };
  };
  const setZoomClamped = (z: number) => {
    const nz = Math.min(MAX_ZOOM, Math.max(1, z));
    setZoom(nz);
    setPan(p => clampPan(p, nz));
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom };
      panStart.current = null;
      pinched.current = true;
    } else {
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
      downPoint.current = { x: e.clientX, y: e.clientY };
      moved.current = false;
      pinched.current = false;
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (downPoint.current && Math.hypot(e.clientX - downPoint.current.x, e.clientY - downPoint.current.y) > TAP_SLOP) {
      moved.current = true;
    }
    if (pointers.current.size >= 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      setZoomClamped(pinchStart.current.zoom * (dist / pinchStart.current.dist));
    } else if (panStart.current && zoom > 1) {
      setPan(clampPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y }, zoom));
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    // Clean tap (no drag/ no pinch) to toggle zoom
    if (pointers.current.size === 0 && !moved.current && !pinched.current) {
      setZoomClamped(zoom === 1 ? 2.5 : 1);
    }
    if (pointers.current.size < 2) pinchStart.current = null;
    const [p] = [...pointers.current.values()];
    panStart.current = p ? { x: p.x - pan.x, y: p.y - pan.y } : null;
  };
  const gesturing = () => pointers.current.size > 0;

  const onKey = useRef<(e: KeyboardEvent) => void>(() => { });
  useEffect(() => {
    onKey.current = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
  });
  useEffect(() => {
    const k = (e: KeyboardEvent) => onKey.current(e);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  return (
    <Box role="dialog" aria-modal="true" sx={{
      position: "fixed", inset: 0, zIndex: 2000, bgcolor: "rgba(0,0,0,.93)",
      display: "flex", flexDirection: "column",
      "@keyframes zvFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
      animation: "zvFadeIn .18s ease-out both",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1.5, color: "#fff" }}>
        <Typography sx={{ flex: 1 }}>{zoneLabel(t, frame.gridReference)} · {hhmm(frame.inspectionDate)} · {index + 1}/{frames.length}</Typography>
        <IconButton aria-label="Zoom out" sx={{ color: "#fff" }} onClick={() => setZoomClamped(zoom - 0.5)}><FiZoomOut aria-hidden="true" /></IconButton>
        <IconButton aria-label="Zoom in" sx={{ color: "#fff" }} onClick={() => setZoomClamped(zoom + 0.5)}><FiZoomIn aria-hidden="true" /></IconButton>
        <IconButton aria-label="Download frame" sx={{ color: "#fff" }} href={url} download><FiDownload aria-hidden="true" /></IconButton>
        <IconButton aria-label="Close viewer" sx={{ color: "#fff" }} onClick={onClose}><FiX aria-hidden="true" /></IconButton>
      </Box>
      <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <IconButton aria-label="Previous frame" onClick={() => go(-1)} sx={{ position: "absolute", left: 8, top: "50%", width: 48, height: 48, color: "#fff", zIndex: 2, bgcolor: "rgba(0,0,0,.35)", "&:hover": { bgcolor: "rgba(0,0,0,.55)" } }}><FiChevronLeft aria-hidden="true" size={28} /></IconButton>
        <IconButton aria-label="Next frame" onClick={() => go(1)} sx={{ position: "absolute", right: 8, top: "50%", width: 48, height: 48, color: "#fff", zIndex: 2, bgcolor: "rgba(0,0,0,.35)", "&:hover": { bgcolor: "rgba(0,0,0,.55)" } }}><FiChevronRight aria-hidden="true" size={28} /></IconButton>
        <Box
          ref={stageRef}
          onWheel={(e) => setZoomClamped(zoom - e.deltaY * 0.002)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none", cursor: zoom > 1 ? "grab" : "zoom-in" }}
        >
          {url && <img src={url} draggable={false}
            alt={`${zoneLabel(t, frame.gridReference)} frame ${index + 1}`}
            style={{
              maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: gesturing() ? "none" : "transform .12s"
            }} />}
        </Box>
      </Box>
      <Filmstrip frames={frames} index={index} getUrl={getUrl} onSelect={selectFrame} />
    </Box>
  );
};

const zoneGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 1.5 } as const;

export const InspectionZoneViewer: React.FC<{
  areas: InspectedArea[];
  getUrl: (fileName: string) => string;
}> = ({ areas, getUrl }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const stack = useMediaQuery(theme.breakpoints.down("sm"));

  const { byZone, counts, maxPerZone } = useMemo(() => groupAreasByZone(areas), [areas]);

  const burst = maxPerZone >= DIAGRAM_MIN_PER_ZONE;
  const firstNonEmpty = counts.findIndex(c => c > 0);
  const [sel, setSel] = useState(firstNonEmpty < 0 ? 0 : firstNonEmpty);
  const [lb, setLb] = useState<{ zone: number; idx: number } | null>(null);

  useEffect(() => { if (counts[sel] === 0 && firstNonEmpty >= 0) setSel(firstNonEmpty); }, [firstNonEmpty]);

  const lightbox = lb && (
    <Lightbox
      frames={byZone[lb.zone] ?? []} getUrl={getUrl} index={lb.idx}
      onIndex={(i) => setLb({ zone: lb.zone, idx: i })} onClose={() => setLb(null)}
    />
  );

  if (!burst) {
    return (
      <Box>
        {byZone.map((frames, z) => frames.length === 0 ? null : (
          <Box key={z} sx={{ mb: 2.5 }}>
            <Typography sx={{ mb: 1, fontWeight: 600 }}>
              {zoneLabel(t, z)}
              <Chip size="small" label={frames.length === 1 ? "1 photo" : `${frames.length} photos`} sx={{ ml: 1 }} />
            </Typography>
            <Box sx={zoneGrid}>
              {frames.map((f, i) => (
                <FrameThumb key={f.id} index={i} url={getUrl(f.photoFileName)} label={hhmm(f.inspectionDate)} onOpen={() => setLb({ zone: z, idx: i })} />
              ))}
            </Box>
          </Box>
        ))}
        {lightbox}
      </Box>
    );
  }

  const frames = byZone[sel] ?? [];
  return (
    <Box sx={{ display: "flex", flexDirection: stack ? "column" : "row", gap: 2, alignItems: "flex-start" }}>
      <Box sx={{
        position: stack ? "static" : "sticky", top: 0, flex: "0 0 auto",
        width: stack ? "100%" : 210, textAlign: "center"
      }}>
        <CarDiagram counts={counts} selected={sel} onSelect={setSel} width={stack ? 180 : 200} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, width: stack ? "100%" : "auto" }}>
        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          {zoneLabel(t, sel)}
          <Chip size="small" label={`${frames.length} frames`} sx={{ ml: 1 }} />
        </Typography>
        {/* key remounts the grid so the stagger replays on zone switch */}
        <Box key={sel} sx={zoneGrid}>
          {frames.map((f, i) => (
            <FrameThumb key={f.id} index={i} url={getUrl(f.photoFileName)} label={hhmm(f.inspectionDate)} onOpen={() => setLb({ zone: sel, idx: i })} />
          ))}
        </Box>
      </Box>

      {lightbox}
    </Box>
  );
};
